import React, { useMemo, useCallback, useContext } from 'react';
import { AppContext } from 'src/components/app-context';
import { PaginationItem as PaginationItemType } from './pagination-items';
import * as S from './styles';
import { IconChevronLeft } from '@prisjakt/ui/icon/chevron-left';
import { IconChevronRight } from '@prisjakt/ui/icon/chevron-right';

type Props = {
  item: PaginationItemType;
  linkToPage: (page: number) => string;
  onClick?: (item: PaginationItemType) => void;
};

const PaginationItem = ({ item, linkToPage, onClick }: Props) => {
  const { t } = useContext(AppContext);
  const label = useMemo(() => {
    switch (item.type) {
      case 'prev':
        return <IconChevronLeft />;
      case 'next':
        return <IconChevronRight />;
      default:
        return <>{item.pageNumber}</>;
    }
  }, [item.type, item.pageNumber]);
  const ariaLabel = useMemo(() => {
    switch (item.type) {
      case 'prev':
        return t('symbols', 'symbol.show-prev');
      case 'next':
        return t('symbols', 'symbol.show-next');
      default:
        return t('common', 'pagination.page-number', {
          number: item.pageNumber,
        });
    }
  }, [item.type, item.pageNumber, t]);
  const onClickHandler = useCallback(
    () => typeof onClick === 'function' && onClick(item),
    [item, onClick]
  );

  if (item.disabled) {
    return <S.DisabledPaginationLink>{label}</S.DisabledPaginationLink>;
  }

  if (item.isCurrent) {
    return (
      <S.CurrentPaginationLink data-test="CurrentPaginationLink">
        {label}
      </S.CurrentPaginationLink>
    );
  }

  return (
    <S.PaginationLink
      to={linkToPage(item.pageNumber)}
      data-test="PaginationLink"
      data-test-page={item.pageNumber}
      onClick={onClickHandler}
      aria-label={ariaLabel}
    >
      {label}
    </S.PaginationLink>
  );
};

export default PaginationItem;
